'use client'

import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { getPlaySettings } from '@/lib/phishtrainer/play-settings'

type Mode = 'guided' | 'expert'

interface Props {
  userId: string
}

export function ModePreferenceCard({ userId }: Props) {
  const [allowToggle, setAllowToggle] = useState<boolean | null>(null)
  const [mode, setMode] = useState<Mode>('guided')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      getPlaySettings(),
      fetch(`/api/phishtrainer/users/${encodeURIComponent(userId)}/preferences`).then((r) => r.json()),
    ])
      .then(([settings, prefs]) => {
        if (cancelled) return
        const allow = settings?.data?.gameplay?.mode?.allowUserToggle === true
        setAllowToggle(allow)
        const adminDefault = settings?.data?.gameplay?.mode?.defaultMode ?? 'guided'
        setMode(prefs?.data?.modeDefault ?? adminDefault)
      })
      .catch(() => setAllowToggle(false))
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [userId])

  if (loading || allowToggle === null) return null
  if (!allowToggle) return null

  async function save(next: Mode) {
    setSaving(true)
    setMode(next)
    try {
      await fetch(`/api/phishtrainer/users/${encodeURIComponent(userId)}/preferences`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ modeDefault: next }),
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Game mode</CardTitle>
        <CardDescription>
          Guided mode shows hints and walks you through each flag, but awards half the points compared to Expert mode.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <RadioGroup
          value={mode}
          onValueChange={(v) => save(v as Mode)}
          className="flex gap-6"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="guided" id="me-mode-guided" disabled={saving} />
            <Label htmlFor="me-mode-guided" className="font-normal">Guided (half points)</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="expert" id="me-mode-expert" disabled={saving} />
            <Label htmlFor="me-mode-expert" className="font-normal">Expert (full points)</Label>
          </div>
        </RadioGroup>
      </CardContent>
    </Card>
  )
}
